/**
 * Ready-made rules per board type, each built only from triggers and actions
 * the engine already runs. A recipe resolves against the board it is opened
 * on, picking the first column of the kind it needs, and says which kind is
 * missing when the board has none, so the gallery can show it unavailable
 * rather than hand the builder a rule that cannot be saved.
 */
import type { TrackerBoardType } from '@/entities/tracker';
import type { AutomationAction, AutomationTrigger } from '@/entities/tracker-automation';

import {
  checkboxColumns,
  dateOnlyColumns,
  dateTriggerColumns,
  hasDateEdges,
  peopleColumns,
  statusColumns,
  type TriggerColumn,
} from './automation-model';
import type { CustomColumn, StatusOption } from './useTrackersBoard';

export type RecipeId =
  | 'stampDateWhenDone'
  | 'assignCreator'
  | 'weeklyCheckboxReset'
  | 'checkWhenDateArrives'
  | 'moveDoneToGroup'
  | 'stampDateOnApproval'
  | 'assignOnChangesRequested';

export type RecipeNeed =
  | 'statusColumn'
  | 'dateColumn'
  | 'checkboxColumn'
  | 'peopleColumn'
  | 'groups';

export interface RecipeBoard {
  columns: CustomColumn[];
  triggerColumns: TriggerColumn[];
  groups: StatusOption[];
}

export type RecipeResult =
  | { rule: { trigger: AutomationTrigger; actions: AutomationAction[] } }
  | { missing: RecipeNeed };

interface Recipe {
  id: RecipeId;
  boardTypes: readonly TrackerBoardType[];
  build: (board: RecipeBoard) => RecipeResult;
}

const ALL_BOARDS: readonly TrackerBoardType[] = ['free_form', 'briefs', 'projects', 'assets'];

/** A status column whose options end in its "finished" state, as the board's own lists do. */
const doneOption = (column: CustomColumn | undefined) => column?.statusOptions?.at(-1);

const RECIPES: readonly Recipe[] = [
  {
    id: 'stampDateWhenDone',
    boardTypes: ALL_BOARDS,
    build: ({ columns, triggerColumns }) => {
      const status = statusColumns(triggerColumns).find((c) => doneOption(c));
      const date = dateOnlyColumns(columns).at(0);
      if (!status) return { missing: 'statusColumn' };
      if (!date) return { missing: 'dateColumn' };
      return {
        rule: {
          trigger: {
            type: 'status_changed',
            columnId: status.id,
            toOptionId: doneOption(status)?.id,
          },
          actions: [{ type: 'set_date_today', columnId: date.id }],
        },
      };
    },
  },
  {
    id: 'assignCreator',
    boardTypes: ['free_form', 'briefs'],
    build: ({ columns }) => {
      const people = peopleColumns(columns).at(0);
      if (!people) return { missing: 'peopleColumn' };
      return {
        rule: {
          trigger: { type: 'item_created' },
          actions: [{ type: 'assign_people', columnId: people.id, userRef: 'actor' }],
        },
      };
    },
  },
  {
    id: 'weeklyCheckboxReset',
    boardTypes: ALL_BOARDS,
    build: ({ columns }) => {
      const checkbox = checkboxColumns(columns).at(0);
      if (!checkbox) return { missing: 'checkboxColumn' };
      return {
        rule: {
          trigger: { type: 'schedule', weekdays: [1], hour: 9 },
          actions: [{ type: 'set_checkbox', columnId: checkbox.id, value: false }],
        },
      };
    },
  },
  {
    id: 'checkWhenDateArrives',
    boardTypes: ALL_BOARDS,
    build: ({ columns, triggerColumns }) => {
      const date = dateTriggerColumns(triggerColumns).at(0);
      const checkbox = checkboxColumns(columns).at(0);
      if (!date) return { missing: 'dateColumn' };
      if (!checkbox) return { missing: 'checkboxColumn' };
      return {
        rule: {
          trigger: {
            type: 'date_arrives',
            columnId: date.id,
            dayOffset: 0,
            ...(hasDateEdges(date) ? { dateEdge: 'end' as const } : {}),
          },
          actions: [{ type: 'set_checkbox', columnId: checkbox.id, value: true }],
        },
      };
    },
  },
  {
    id: 'moveDoneToGroup',
    boardTypes: ['free_form'],
    build: ({ columns, groups }) => {
      const status = statusColumns(columns).find((c) => doneOption(c));
      const group = groups.at(-1);
      if (!status) return { missing: 'statusColumn' };
      if (!group) return { missing: 'groups' };
      return {
        rule: {
          trigger: {
            type: 'status_changed',
            columnId: status.id,
            toOptionId: doneOption(status)?.id,
          },
          actions: [{ type: 'move_to_group', groupId: group.id }],
        },
      };
    },
  },
  {
    id: 'stampDateOnApproval',
    boardTypes: ['assets'],
    build: ({ columns }) => {
      const date = dateOnlyColumns(columns).at(0);
      if (!date) return { missing: 'dateColumn' };
      return {
        rule: {
          trigger: { type: 'approval_event', approvalEvent: 'approved' },
          actions: [{ type: 'set_date_today', columnId: date.id }],
        },
      };
    },
  },
  {
    id: 'assignOnChangesRequested',
    boardTypes: ['assets'],
    build: ({ columns }) => {
      const people = peopleColumns(columns).at(0);
      if (!people) return { missing: 'peopleColumn' };
      return {
        rule: {
          trigger: { type: 'approval_event', approvalEvent: 'changes_requested' },
          actions: [{ type: 'assign_people', columnId: people.id, userRef: 'actor' }],
        },
      };
    },
  },
];

/** The recipes a board of this type offers, each resolved against the board. */
export function recipesFor(
  boardType: TrackerBoardType,
  board: RecipeBoard,
): Array<{ id: RecipeId } & RecipeResult> {
  return RECIPES.filter((recipe) => recipe.boardTypes.includes(boardType)).map((recipe) => ({
    id: recipe.id,
    ...recipe.build(board),
  }));
}
